import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';
import { useAppSelector } from '../../../store';
import Button from '../../../components/Button';
import TextField from '../../../components/TextField';
import Toast from '../../../components/Toast';
import Icon from '../../../components/Icon';
import { FiMail, FiDownload, FiCalendar, FiSend, FiPlus, FiX, FiFileText } from 'react-icons/fi';
import { sendEmailsWithErrorHandling, validateEmails } from '../../../api/emailService';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';


// Helper: Format integers to uppercase words
function numberToWords(num: number): string {
    const words = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
    const tens = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
    if (num < 20) return words[num] || String(num);
    if (num < 100) {
        const digit = num % 10;
        return tens[Math.floor(num / 10)] + (digit ? `-${words[digit]}` : "");
    }
    return String(num);
}

export default function WeeklyReport() {
    const bank = useAppSelector((s) => s.user.bank);
    const pso = useAppSelector((s) => s.user.pso);
    const companyId = bank?.bankDocId || sessionStorage.getItem('bankDocId') || bank?.companyId || '';

    // Date Range (default: last 7 days)
    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().split('T')[0];
    });
    const [dateTo, setDateTo] = useState(() => {
        return new Date().toISOString().split('T')[0];
    });

    const [loading, setLoading] = useState(false);
    const [emailSending, setEmailSending] = useState(false);

    // Toast States
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'success' | 'error' | 'warning' | 'info'>('success');
    const [showToast, setShowToast] = useState(false);

    // Email Config States
    const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
    const [emailDraft, setEmailDraft] = useState('');
    const [emailSubject, setEmailSubject] = useState('Weekly Approved Request Summary Report');
    const [customMessage, setCustomMessage] = useState('');

    // Fetched request groups
    const [guardRequests, setGuardRequests] = useState<any[]>([]);
    const [cctvFootageRequests, setCctvFootageRequests] = useState<any[]>([]);
    const [cctvHardwareRequests, setCctvHardwareRequests] = useState<any[]>([]);
    const [bamsHardwareRequests, setBamsHardwareRequests] = useState<any[]>([]);
    const [branchesMap, setBranchesMap] = useState<Record<string, any>>({});

    const triggerToast = (msg: string, type: 'success' | 'error' | 'warning' | 'info' = 'success') => {
        setToastMessage(msg);
        setToastType(type);
        setShowToast(true);
    };

    // Helper: Parse Firestore date formats
    const parseDateValue = (raw: any): Date | null => {
        if (!raw) return null;
        if (raw instanceof Date) return raw;
        if (typeof raw.toDate === 'function') {
            try {
                return raw.toDate();
            } catch {
                return null;
            }
        }
        if (typeof raw.seconds === 'number') {
            return new Date(raw.seconds * 1000);
        }
        const d = new Date(raw);
        return isNaN(d.getTime()) ? null : d;
    };

    // Helper: Resolve Request Date (prefer approvedAt, fallback to updatedAt if approved, fallback to createdAt, fallback to requestDate fields)
    const getRequestDate = (req: any): Date | null => {
        if (req.approvedAt) return parseDateValue(req.approvedAt);
        if (req.status?.toLowerCase() === 'approved' && req.updatedAt) return parseDateValue(req.updatedAt);
        if (req.createdAt) return parseDateValue(req.createdAt);
        if (req.dateOfRequest) return new Date(req.dateOfRequest);
        if (req.guardDateFrom) return parseDateValue(req.guardDateFrom);
        if (req.cctvDateFrom) return parseDateValue(req.cctvDateFrom);
        return null;
    };

    // Helper to get formatted location data from request using branchesMap
    const getRequestLocationData = (req: any) => {
        const b = branchesMap[req.branchId] || branchesMap[req.branchCode];
        if (!b) {
            return {
                locationName: req.region || 'N/A',
                coord: '—',
                distance: ''
            };
        }
        
        let lat = '';
        let lng = '';
        let distance = '';
        if (b.location && (b.location.lat != null || b.location.long != null)) {
            if (b.location.lat != null) lat = String(b.location.lat);
            if (b.location.long != null) lng = String(b.location.long);
            distance = (b.location.distanceFromRegion || '').trim();
        }
        if ((!lat || !lng) && b.latitudeLongitude) {
            const parts = b.latitudeLongitude.split(/[,\s]+/).map((p: string) => p.trim()).filter(Boolean);
            if (parts.length >= 2) {
                lat = lat || parts[0];
                lng = lng || parts[1];
            }
        }
        if (!distance && b.distanceFromRegion) distance = String(b.distanceFromRegion).trim();
        
        const numLat = Number(lat);
        const numLng = Number(lng);
        const coord = !isNaN(numLat) && !isNaN(numLng) && lat && lng
            ? `${numLat.toFixed(5)}, ${numLng.toFixed(5)}`
            : '—';
        return {
            locationName: b.locationName || req.region || '—',
            coord,
            distance
        };
    };

    // Fetch and filter requests
    const fetchReportData = async () => {
        if (!companyId) return;
        setLoading(true);

        try {
            // Fetch branches and locations to build the branchesMap
            const branchesSnap = await getDocs(collection(db, 'banks', companyId, 'branch'));
            const locSnap = await getDocs(collection(db, 'locations'));
            const locList = locSnap.docs.map(d => ({ id: d.id, ...d.data() } as { id: string; name: string }));

            const bMap: Record<string, any> = {};
            branchesSnap.forEach(d => {
                const data = d.data();
                const locationName = locList.find(l => l.id === data.locationId)?.name || 'N/A';
                const branchObj = { id: d.id, ...data, locationName };
                bMap[d.id] = branchObj;
                if (data.branchCode) {
                    bMap[data.branchCode] = branchObj;
                }
            });
            setBranchesMap(bMap);

            // Set up date range boundaries
            const fromStart = new Date(dateFrom);
            fromStart.setHours(0, 0, 0, 0);

            const toEnd = new Date(dateTo);
            toEnd.setHours(23, 59, 59, 999);

            // 1. Fetch general requests collection (CCTV Footage & Guard)
            const requestsSnap = await getDocs(
                query(collection(db, 'banks', companyId, 'requests'), orderBy('createdAt', 'desc'))
            );

            const allGeneralRequests = requestsSnap.docs.map(docSnap => ({
                id: docSnap.id,
                ...docSnap.data()
            })) as any[];

            // Filter approved requests in date range
            const approvedGeneral = allGeneralRequests.filter(req => {
                if (req.status?.toLowerCase() !== 'approved') return false;

                // Filter only requests created by the current PSO who is generating the report
                const userRole = sessionStorage.getItem('role');
                const isPsoUser = userRole === 'pso' || userRole === 'incharge' || !!pso;
                const creatorUid = pso?.authUid || auth.currentUser?.uid;
                if (isPsoUser && creatorUid && req.createdBy !== creatorUid) return false;

                const reqDate = getRequestDate(req);
                if (!reqDate) return false;
                return reqDate >= fromStart && reqDate <= toEnd;
            });

            // Split into Guard and CCTV Footage (case-insensitive)
            const guards = approvedGeneral.filter(req => req.requestType?.toLowerCase() === 'guard');
            const footages = approvedGeneral.filter(req => req.requestType?.toLowerCase() === 'cctv');

            // 2. Fetch hardware_requests collection (CCTV & BAMS hardware)
            const hardwareSnap = await getDocs(
                query(collection(db, 'banks', companyId, 'hardware_requests'), orderBy('createdAt', 'desc'))
            );

            const allHardwareRequests = await Promise.all(
                hardwareSnap.docs.map(async (docSnap) => {
                    const data = { id: docSnap.id, ...docSnap.data() } as any;
                    // Load sub-collection items if not embedded
                    if (!data.items || data.items.length === 0) {
                        try {
                            const itemsSnap = await getDocs(
                                collection(db, 'banks', companyId, 'hardware_requests', docSnap.id, 'items')
                            );
                            data.items = itemsSnap.docs.map(i => ({ id: i.id, ...i.data() }));
                        } catch {
                            data.items = [];
                        }
                    }
                    return data;
                })
            );

            // Filter approved hardware requests in date range
            const approvedHardware = allHardwareRequests.filter(req => {
                if (req.status?.toLowerCase() !== 'approved' || req.docType?.toLowerCase() !== 'pso_request') return false;

                // Filter only requests created by the current PSO who is generating the report
                const userRole = sessionStorage.getItem('role');
                const isPsoUser = userRole === 'pso' || userRole === 'incharge' || !!pso;
                const creatorUid = pso?.authUid || auth.currentUser?.uid;
                if (isPsoUser && creatorUid && req.createdBy !== creatorUid) return false;

                const reqDate = getRequestDate(req);
                if (!reqDate) return false;
                return reqDate >= fromStart && reqDate <= toEnd;
            });

            // Split into CCTV Hardware and BAMS Hardware (case-insensitive)
            const cctvHw = approvedHardware.filter(req => req.requestType?.toUpperCase() === 'CCTV' || req.isAccessControl === true);
            const bamsHw = approvedHardware.filter(req => req.requestType?.toUpperCase() === 'BAMS');

            setGuardRequests(guards);
            setCctvFootageRequests(footages);
            setCctvHardwareRequests(cctvHw);
            setBamsHardwareRequests(bamsHw);

            triggerToast('Report data generated successfully!', 'success');
        } catch (error) {
            console.error('Error fetching report data:', error);
            triggerToast('Failed to load report data', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Auto-fetch data on initial mount or when dates are confirmed
    useEffect(() => {
        fetchReportData();
    }, [companyId]);

    // Format specifications for CCTV Hardware request
    const getCctvSpecsText = (req: any) => {
        if (!req.items || req.items.length === 0) {
            return req.remarks || 'CCTV equipment provision request';
        }
        const itemsStr = req.items.map((it: any) => {
            const qty = Number(it.quantity) || 1;
            const qtyWord = numberToWords(qty).toUpperCase();
            const name = it.itemName || '';
            const spec = it.specification ? ` (${it.specification})` : '';
            return `${qtyWord} ${name}${spec}`;
        }).join(', ');

        return `Installation/ Provision of "${itemsStr} to be installed at ${req.branchName || 'N/A'} (${req.branchCode || 'N/A'})"`;
    };

    // Format specifications for BAMS Hardware request
    const getBamsSpecsText = (req: any) => {
        if (!req.items || req.items.length === 0) {
            return req.remarks || 'BAMS equipment installation request';
        }
        const itemsStr = req.items.map((it: any) => {
            const qty = Number(it.quantity) || 1;
            const qtyWord = numberToWords(qty).toUpperCase();
            const name = it.itemName || '';
            const spec = it.specification ? ` (${it.specification})` : '';
            return `${qtyWord} ${name}${spec}`;
        }).join(', ');

        const totalAmt = req.amount || req.totalAmount || 0;
        const vendorName = req.vendor || 'Concerned BAMS Vendor';

        return `Sanction for Installation/ Replacement/ Provision of "${itemsStr} involving an amount sum of Rs ${totalAmt}/- as per quotation submitted by ${vendorName}, however the same item yet not covered at PSO App," is suggested to be Approved please.`;
    };

    const cleanBranchName = (branchName: string, bankName: string) => {
        let name = branchName || '';
        if (bankName) {
            const escapedBankName = bankName.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
            const regex = new RegExp(`^${escapedBankName}\\s*[-–—]?\\s*`, 'i');
            name = name.replace(regex, '');
        }
        return name;
    };

    const formatBranchDetailsHtml = (req: any, bankName: string) => {
        const code = req.branchCode || 'N/A';
        const rawName = req.branchName || 'N/A';
        const cleanedName = rawName !== 'N/A' ? cleanBranchName(rawName, bankName) : rawName;
        const { locationName } = getRequestLocationData(req);
        return `${code}<br/>${cleanedName}<br/>${locationName || '—'}`;
    };

    const formatBranchDetailsPdf = (req: any, bankName: string) => {
        const code = req.branchCode || 'N/A';
        const rawName = req.branchName || 'N/A';
        const cleanedName = rawName !== 'N/A' ? cleanBranchName(rawName, bankName) : rawName;
        const { locationName } = getRequestLocationData(req);
        return `${code}\n${cleanedName}\n${locationName || '—'}`;
    };

    const formatPurposeText = (req: any) => {
        const purpose = (req.purpose || '').trim();
        const otherText = (req.purposeOther || req.purpose_other || '').trim();
        if (purpose.toLowerCase() === 'other' || purpose.toLowerCase() === 'police pressure') {
            return otherText || purpose || 'N/A';
        }
        return purpose || otherText || 'N/A';
    };

    const formatDateStr = (dateStr: string) => {
        if (!dateStr) return 'N/A';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        return `${day}-${month}-${d.getFullYear()}`;
    };

    // Render HTML for the Preview panel & Email sending
    const reportHtml = useMemo(() => {
        const getPeriodStr = (req: any) => {
            const from = req.guardDateFrom || req.cctvDateFrom;
            const to = req.guardDateTo || req.cctvDateTo;
            if (!from && !to) return 'N/A';
            return `${formatDateStr(from)} to ${formatDateStr(to)}`;
        };

        const getCctvDateStr = (req: any) => {
            const from = req.cctvDateFrom;
            const to = req.cctvDateTo;
            if (!from && !to) return 'N/A';
            return `${formatDateStr(from)} to ${formatDateStr(to)}`;
        };

        const psoName = pso?.name || auth.currentUser?.displayName || 'N/A';
        const psoRegion = (() => {
            if ((pso as any)?.region) return (pso as any).region;
            const allReqs = [...guardRequests, ...cctvFootageRequests, ...cctvHardwareRequests, ...bamsHardwareRequests];
            const found = allReqs.find(r => r.region);
            if (found) return found.region;
            if (pso?.designation) {
                return pso.designation.replace(/_/g, ' ');
            }
            return 'N/A';
        })();
        const reportDate = formatDateStr(new Date().toISOString().split('T')[0]);

        const formatHtmlLocation = (req: any) => {
            const { locationName } = getRequestLocationData(req);
            return locationName || '—';
        };


        const guardRows = guardRequests.length > 0 ? guardRequests.map((req, index) => `
            <tr>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${index + 1}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${formatBranchDetailsHtml(req, bank?.companyName || '')}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${req.numberOfGuards || 0}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${req.guardCompany || req.company || 'N/A'}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${getPeriodStr(req)}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${formatPurposeText(req)}</td>
            </tr>
        `).join('') : `
            <tr>
                <td colspan="6" style="padding: 12px; border: 1px solid #ddd; text-align: center; color: #666; font-style: italic;">No request</td>
            </tr>
        `;

        const footageRows = cctvFootageRequests.length > 0 ? cctvFootageRequests.map((req, index) => `
            <tr>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${index + 1}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${formatBranchDetailsHtml(req, bank?.companyName || '')}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${req.company || 'N/A'}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${req.timeFrom || 'N/A'} to ${req.timeTo || 'N/A'}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${getCctvDateStr(req)}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${formatPurposeText(req)}</td>
            </tr>
        `).join('') : `
            <tr>
                <td colspan="6" style="padding: 12px; border: 1px solid #ddd; text-align: center; color: #666; font-style: italic;">No request</td>
            </tr>
        `;

        const cctvHardwareRows = cctvHardwareRequests.length > 0 ? cctvHardwareRequests.map((req, index) => `
            <tr>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${index + 1}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${formatBranchDetailsHtml(req, bank?.companyName || '')}</td>
                <td style="padding: 8px; border: 1px solid #ddd; line-height: 1.4;">${getCctvSpecsText(req)}</td>
            </tr>
        `).join('') : `
            <tr>
                <td colspan="3" style="padding: 12px; border: 1px solid #ddd; text-align: center; color: #666; font-style: italic;">No request</td>
            </tr>
        `;

        const bamsHardwareRows = bamsHardwareRequests.length > 0 ? bamsHardwareRequests.map((req, index) => `
            <tr>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${index + 1}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${formatBranchDetailsHtml(req, bank?.companyName || '')}</td>
                <td style="padding: 8px; border: 1px solid #ddd; line-height: 1.4;">${getBamsSpecsText(req)}</td>
            </tr>
        `).join('') : `
            <tr>
                <td colspan="3" style="padding: 12px; border: 1px solid #ddd; text-align: center; color: #666; font-style: italic;">No request</td>
            </tr>
        `;

        return `
        <!DOCTYPE html>
        <html>
        <head>
            <meta charset="UTF-8">
            <style>
                body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 20px; }
                .container { max-width: 800px; margin: auto; border: 1px solid #ddd; padding: 25px; border-radius: 8px; background: #fff; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
                .header { text-align: center; margin-bottom: 25px; border-bottom: 3px solid #00838f; padding-bottom: 15px; }
                .header h2 { color: #00838f; margin: 0; font-size: 20px; font-weight: bold; letter-spacing: 0.5px; }
                .header p { color: #555; margin: 5px 0 0; font-size: 14px; }
                .custom-msg { font-size: 14px; color: #333; margin-bottom: 25px; white-space: pre-wrap; line-height: 1.5; }
                .section-title { color: #1e3a5f; border-left: 4px solid #00838f; padding-left: 10px; font-size: 15px; font-weight: bold; margin-top: 30px; margin-bottom: 12px; }
                .table-container { width: 100%; overflow-x: auto; margin-bottom: 25px; }
                table { width: 100%; border-collapse: collapse; font-size: 11px; border: 1px solid #ddd; }
                th { background-color: #1e3a5f; color: #fff; padding: 8px; border: 1px solid #ddd; font-weight: bold; text-align: left; }
                th.center { text-align: center; }
                td { padding: 8px; border: 1px solid #ddd; vertical-align: top; }
                .footer { margin-top: 35px; border-top: 1px solid #eee; padding-top: 15px; font-size: 11px; color: #777; text-align: center; }
            </style>
        </head>
        <body>
            <div class="container">
                <div class="header">
                    <h2>APPROVED REQUESTS</h2>
                    <p>Period: ${formatDateStr(dateFrom)} to ${formatDateStr(dateTo)}</p>
                    <div style="margin-top: 12px; font-size: 11px; color: #555; display: flex; justify-content: center; gap: 15px; flex-wrap: wrap; border-top: 1px solid #eee; padding-top: 8px;">
                        <span><strong>PSO Name:</strong> ${psoName}</span>
                        <span><strong>Report Date:</strong> ${reportDate}</span>
                    </div>
                </div>
                
                ${customMessage ? `<div class="custom-msg">${customMessage}</div>` : ''}
                
                <div class="section-title">Additional Guard deployments</div>
                <div class="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th class="center" style="width: 50px;">S. No.</th>
                                <th style="width: 160px;">Branch Details</th>
                                <th class="center" style="width: 75px;">No. of Guards</th>
                                <th style="width: 100px;">Company</th>
                                <th class="center" style="width: 130px;">Period (From - To)</th>
                                <th>Reason</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${guardRows}
                        </tbody>
                    </table>
                </div>
                
                <div class="section-title">Retrieval of CCTV Footages</div>
                <div class="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th class="center" style="width: 50px;">S. No.</th>
                                <th style="width: 160px;">Branch Details</th>
                                <th style="width: 100px;">Company</th>
                                <th class="center" style="width: 110px;">Time (From - To)</th>
                                <th class="center" style="width: 130px;">Date (From - To)</th>
                                <th>Purpose</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${footageRows}
                        </tbody>
                    </table>
                </div>
                
                <div class="section-title">CCTV Equipment Provision/ Re-Utilization</div>
                <div class="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th class="center" style="width: 50px;">S. No.</th>
                                <th style="width: 160px;">Branch Details</th>
                                <th>Specs/ Details of the equipment to be installed</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${cctvHardwareRows}
                        </tbody>
                    </table>
                </div>
                
                <div class="section-title">BAMS Equipment Installations</div>
                <div class="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th class="center" style="width: 50px;">S. No.</th>
                                <th style="width: 160px;">Branch Details</th>
                                <th>Specs/ Details of the equipment to be installed</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${bamsHardwareRows}
                        </tbody>
                    </table>
                </div>
                
                <div class="footer">
                    <p>This is an automated weekly summary report generated by the PSO Request Management System.</p>
                </div>
            </div>
        </body>
        </html>
        `;
    }, [guardRequests, cctvFootageRequests, cctvHardwareRequests, bamsHardwareRequests, customMessage, dateFrom, dateTo, pso, branchesMap]);

    // Handle adding recipients from input
    const handleAddRecipient = (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = emailDraft.trim();
        if (!trimmed) return;

        // Support comma/semicolon/newline separation
        const emails = trimmed
            .split(/[,;\n]/)
            .map(email => email.trim())
            .filter(email => email.length > 0);

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const valid = emails.filter(email => emailRegex.test(email));
        const invalid = emails.filter(email => !emailRegex.test(email));

        if (invalid.length > 0) {
            triggerToast(`Invalid email format: ${invalid.join(', ')}`, 'error');
            return;
        }

        setEmailRecipients(prev => {
            const next = [...prev];
            valid.forEach(email => {
                if (!next.includes(email)) next.push(email);
            });
            return next;
        });
        setEmailDraft('');
    };

    const handleRemoveRecipient = (email: string) => {
        setEmailRecipients(prev => prev.filter(e => e !== email));
    };

    // Send Email Handler
    const handleSendEmail = async () => {
        if (emailRecipients.length === 0) {
            triggerToast('Please add at least one recipient email address.', 'warning');
            return;
        }

        setEmailSending(true);
        try {
            const results = await sendEmailsWithErrorHandling({
                to: emailRecipients,
                subject: emailSubject,
                body: reportHtml,
                isHtml: true,
                companyName: 'HBL PSO Reporting Service'
            });

            if (results.failed.length > 0 && results.successful.length === 0) {
                triggerToast(`Email delivery failed: ${results.failed.map(f => f.email).join(', ')}`, 'error');
            } else if (results.failed.length > 0) {
                triggerToast(`Sent to ${results.successful.length}. Failed: ${results.failed.map(f => f.email).join(', ')}`, 'warning');
            } else {
                triggerToast(`Email sent successfully to ${results.successful.length} recipient(s).`, 'success');
            }
        } catch (error: any) {
            console.error('Error sending email:', error);
            triggerToast(error?.message || 'Failed to send email summary', 'error');
        } finally {
            setEmailSending(false);
        }
    };

    // Download PDF Handler
    const handleDownloadPdf = () => {
        try {
            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

            // 1. Report Title
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(16);
            doc.setTextColor(0, 131, 143); // HBL Teal
            doc.text('APPROVED REQUESTS', 15, 15);

            const psoName = pso?.name || auth.currentUser?.displayName || 'N/A';
            const psoRegion = (() => {
                if ((pso as any)?.region) return (pso as any).region;
                const allReqs = [...guardRequests, ...cctvFootageRequests, ...cctvHardwareRequests, ...bamsHardwareRequests];
                const found = allReqs.find(r => r.region);
                if (found) return found.region;
                if (pso?.designation) {
                    return pso.designation.replace(/_/g, ' ');
                }
                return 'N/A';
            })();
            const reportDate = formatDateStr(new Date().toISOString().split('T')[0]);

            // 2. Subtitle / Details
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(80, 80, 80);
            doc.text(`Period: ${formatDateStr(dateFrom)} to ${formatDateStr(dateTo)}`, 15, 21);
            doc.text(`PSO Name: ${psoName}`, 15, 25);
            doc.text(`Report Date: ${reportDate}`, 15, 33);

            // Custom Message Block
            let currentY = 38;
            if (customMessage) {
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 0);
                const splitMsg = doc.splitTextToSize(customMessage, 267);
                doc.text(splitMsg, 15, 38);
                currentY = 38 + (splitMsg.length * 5) + 5;
            }

            // Render Temporary Guards Deployments Table
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(30, 58, 95);
            doc.text('1. Additional Guard deployments', 15, currentY);

            const getPeriodStr = (req: any) => {
                const from = req.guardDateFrom || req.cctvDateFrom;
                const to = req.guardDateTo || req.cctvDateTo;
                if (!from && !to) return 'N/A';
                return `${formatDateStr(from)} to ${formatDateStr(to)}`;
            };

            const getPdfLocationLines = (req: any) => {
                const { locationName } = getRequestLocationData(req);
                return locationName || '—';
            };

            autoTable(doc, {
                startY: currentY + 2,
                head: [['S. No.', 'Branch Details', 'No. of Guards', 'Company', 'Period (From - To)', 'Reason']],
                body: guardRequests.length > 0 ? guardRequests.map((req, i) => [
                    String(i + 1),
                    formatBranchDetailsPdf(req, bank?.companyName || ''),
                    String(req.numberOfGuards || 0),
                    req.guardCompany || req.company || 'N/A',
                    getPeriodStr(req),
                    formatPurposeText(req)
                ]) : [['-', 'No request', '-', '-', '-', '-']],
                theme: 'grid',
                styles: { fontSize: 7.5, cellPadding: 1.5, lineColor: [200, 200, 200], lineWidth: 0.1 },
                headStyles: { fillColor: [30, 58, 95], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [252, 252, 253] },
                columnStyles: {
                    0: { cellWidth: 12, halign: 'center' },
                    1: { cellWidth: 45 },
                    2: { cellWidth: 20, halign: 'center' },
                    3: { cellWidth: 35 },
                    4: { cellWidth: 45, halign: 'center' },
                    5: { cellWidth: 'auto' }
                },
                margin: { left: 15, right: 15 }
            });

            currentY = (doc as any).lastAutoTable.finalY + 10;

            // Render CCTV Footage Table
            if (currentY > 165) {
                doc.addPage();
                currentY = 15;
            }

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(30, 58, 95);
            doc.text('2. Retrieval of CCTV Footages', 15, currentY);

            const getCctvDateStr = (req: any) => {
                const from = req.cctvDateFrom;
                const to = req.cctvDateTo;
                if (!from && !to) return 'N/A';
                return `${formatDateStr(from)} to ${formatDateStr(to)}`;
            };

            autoTable(doc, {
                startY: currentY + 2,
                head: [['S. No.', 'Branch Details', 'Company', 'Time (From - To)', 'Date (From - To)', 'Purpose']],
                body: cctvFootageRequests.length > 0 ? cctvFootageRequests.map((req, i) => [
                    String(i + 1),
                    formatBranchDetailsPdf(req, bank?.companyName || ''),
                    req.company || 'N/A',
                    `${req.timeFrom || 'N/A'} to ${req.timeTo || 'N/A'}`,
                    getCctvDateStr(req),
                    formatPurposeText(req)
                ]) : [['-', 'No request', '-', '-', '-', '-']],
                theme: 'grid',
                styles: { fontSize: 7.5, cellPadding: 1.5, lineColor: [200, 200, 200], lineWidth: 0.1 },
                headStyles: { fillColor: [30, 58, 95], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [252, 252, 253] },
                columnStyles: {
                    0: { cellWidth: 12, halign: 'center' },
                    1: { cellWidth: 45 },
                    2: { cellWidth: 35 },
                    3: { cellWidth: 35, halign: 'center' },
                    4: { cellWidth: 45, halign: 'center' },
                    5: { cellWidth: 'auto' }
                },
                margin: { left: 15, right: 15 }
            });

            currentY = (doc as any).lastAutoTable.finalY + 10;

            // Render CCTV Hardware Provision Table
            if (currentY > 165) {
                doc.addPage();
                currentY = 15;
            }

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(30, 58, 95);
            doc.text('3. CCTV Equipment Provision/ Re-Utilization', 15, currentY);

            autoTable(doc, {
                startY: currentY + 2,
                head: [['S. No.', 'Branch Details', 'Specs/ Details of the equipment to be installed']],
                body: cctvHardwareRequests.length > 0 ? cctvHardwareRequests.map((req, i) => [
                    String(i + 1),
                    formatBranchDetailsPdf(req, bank?.companyName || ''),
                    getCctvSpecsText(req)
                ]) : [['-', 'No request', '-']],
                theme: 'grid',
                styles: { fontSize: 7.5, cellPadding: 1.8, lineColor: [200, 200, 200], lineWidth: 0.1 },
                headStyles: { fillColor: [30, 58, 95], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [252, 252, 253] },
                columnStyles: {
                    0: { cellWidth: 15, halign: 'center' },
                    1: { cellWidth: 50 },
                    2: { cellWidth: 'auto' }
                },
                margin: { left: 15, right: 15 }
            });

            currentY = (doc as any).lastAutoTable.finalY + 10;

            // Render BAMS Hardware Provision Table
            if (currentY > 165) {
                doc.addPage();
                currentY = 15;
            }

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(30, 58, 95);
            doc.text('4. BAMS Equipment Installations', 15, currentY);

            autoTable(doc, {
                startY: currentY + 2,
                head: [['S. No.', 'Branch Details', 'Specs/ Details of the equipment to be installed']],
                body: bamsHardwareRequests.length > 0 ? bamsHardwareRequests.map((req, i) => [
                    String(i + 1),
                    formatBranchDetailsPdf(req, bank?.companyName || ''),
                    getBamsSpecsText(req)
                ]) : [['-', 'No request', '-']],
                theme: 'grid',
                styles: { fontSize: 7.5, cellPadding: 1.8, lineColor: [200, 200, 200], lineWidth: 0.1 },
                headStyles: { fillColor: [30, 58, 95], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [252, 252, 253] },
                columnStyles: {
                    0: { cellWidth: 15, halign: 'center' },
                    1: { cellWidth: 50 },
                    2: { cellWidth: 'auto' }
                },
                margin: { left: 15, right: 15 }
            });

            // Draw page numbers
            const totalPages = doc.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8);
                doc.setTextColor(130, 130, 130);
                doc.text(`Page ${i} of ${totalPages}`, 297 - 15, 210 - 8, { align: 'right' });
            }

            doc.save(`Weekly_Requests_Summary_${dateFrom}_to_${dateTo}.pdf`);
            triggerToast('PDF generated and downloaded successfully!', 'success');
        } catch (error: any) {
            console.error('Error generating PDF:', error);
            triggerToast('Failed to generate PDF file', 'error');
        }
    };

    return (
        <div className="flex flex-col gap-6 p-4 md:p-6 h-auto lg:h-[calc(100vh-64px)] lg:overflow-hidden">
            {/* Header section */}
            <div className="flex flex-col gap-2 shrink-0 md:flex-row md:items-center md:justify-between border-b border-gray-200 dark:border-gray-800 pb-4">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Weekly Requests Report</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Generate, export, and email weekly request summary reports for CCTV, Guard, and BAMS modules.</p>
                </div>
            </div>

            {/* Main Content Workspace */}
            <div className="flex-1 flex flex-col lg:flex-row gap-6 min-h-0 lg:overflow-hidden">
                {/* Left Side: Filter and configuration controls */}
                <div className="w-full lg:w-96 flex flex-col gap-6 shrink-0 lg:overflow-y-auto pr-1">
                    {/* Date Filter Card */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-950 flex flex-col gap-4">
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-2">
                            <Icon icon={FiCalendar} className="w-4 h-4 text-blue-600" />
                            Date Selection
                        </h2>
                        
                        <div className="flex flex-col gap-1">
                            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">DATE RANGE</label>
                            <DatePicker.RangePicker
                                className="w-full min-h-[40px] [&_.ant-picker]:w-full [&_.ant-picker-input>input]:text-sm rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                                value={dateFrom && dateTo ? [dayjs(dateFrom), dayjs(dateTo)] : null}
                                format="MMM D, YYYY"
                                allowClear={false}
                                onChange={(dates) => {
                                    if (dates && dates[0] && dates[1]) {
                                        setDateFrom(dates[0].format('YYYY-MM-DD'));
                                        setDateTo(dates[1].format('YYYY-MM-DD'));
                                    }
                                }}
                            />
                        </div>

                        <Button onClick={fetchReportData} disabled={loading} className="w-full flex items-center justify-center gap-2">
                            {loading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Fetching Data...</span>
                                </>
                            ) : (
                                <span>Update Filter</span>
                            )}
                        </Button>
                    </div>

                    {/* Email Config Card */}
                    <div className="flex-1 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-950 flex flex-col gap-4 min-h-[350px]">
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-2">
                            <Icon icon={FiMail} className="w-4 h-4 text-blue-600" />
                            Email Configuration
                        </h2>

                        {/* Subject */}
                        <div className="flex flex-col gap-1">
                            <TextField
                                label="Email Subject"
                                value={emailSubject}
                                onChange={(e) => setEmailSubject(e.target.value)}
                                className="w-full text-sm"
                            />
                        </div>

                        {/* Custom Message / Intro */}
                        <div className="flex flex-col gap-1">
                            <label className="block text-xs font-normal uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                Prepend Custom Message
                            </label>
                            <textarea
                                value={customMessage}
                                onChange={(e) => setCustomMessage(e.target.value)}
                                rows={3}
                                className="w-full p-3 text-sm rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none"
                                placeholder="Enter introductory remarks for the email body..."
                            />
                        </div>

                        {/* Recipients Tag Input */}
                        <div className="flex flex-col gap-2">
                            <label className="block text-xs font-normal uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                Recipient Email(s)
                            </label>
                            <form onSubmit={handleAddRecipient} className="flex gap-2">
                                <input
                                    type="text"
                                    value={emailDraft}
                                    onChange={(e) => setEmailDraft(e.target.value)}
                                    placeholder="Add email address..."
                                    className="flex-1 h-10 px-3 text-sm rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                                />
                                <Button type="submit" variant="secondary" className="shrink-0 h-10">
                                    <Icon icon={FiPlus} className="w-5 h-5" />
                                </Button>
                            </form>

                            {/* Email tags list */}
                            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-1 border border-gray-100 dark:border-gray-800 rounded-xl bg-gray-50 dark:bg-gray-900/30">
                                {emailRecipients.length === 0 ? (
                                    <span className="text-xs text-gray-400 p-1.5 italic">No recipients added yet</span>
                                ) : (
                                    emailRecipients.map(email => (
                                        <span key={email} className="inline-flex items-center gap-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-lg text-xs font-medium border border-blue-100 dark:border-blue-900/50">
                                            {email}
                                            <button type="button" onClick={() => handleRemoveRecipient(email)} className="hover:text-blue-950 dark:hover:text-white transition">
                                                <Icon icon={FiX} className="w-3.5 h-3.5" />
                                            </button>
                                        </span>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Send Email Action Button */}
                        <Button
                            onClick={handleSendEmail}
                            disabled={emailSending || loading || emailRecipients.length === 0}
                            className="mt-auto w-full flex items-center justify-center gap-2"
                        >
                            {emailSending ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Sending Emails...</span>
                                </>
                            ) : (
                                <>
                                    <Icon icon={FiSend} className="w-4 h-4" />
                                    <span>Send Summary Email</span>
                                </>
                            )}
                        </Button>
                    </div>
                </div>

                {/* Right Side: Professional Email / Document Live Preview */}
                <div className="flex-1 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-950 flex flex-col gap-4 min-h-[500px] lg:min-h-0 lg:overflow-hidden min-w-0">
                    <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-3 shrink-0">
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-2">
                            <Icon icon={FiFileText} className="w-4 h-4 text-blue-600" />
                            Report Live Preview
                        </h2>

                        <Button onClick={handleDownloadPdf} variant="secondary" className="flex items-center gap-2 !py-2">
                            <Icon icon={FiDownload} className="w-4 h-4" />
                            <span>Download PDF</span>
                        </Button>
                    </div>

                    {/* Preview Iframe Container */}
                    <div className="flex-1 border border-gray-200 dark:border-gray-800 rounded-xl bg-gray-50 dark:bg-gray-900 overflow-hidden relative">
                        {loading ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-gray-950/70 z-10">
                                <div className="flex flex-col items-center gap-2">
                                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Generating report preview...</span>
                                </div>
                            </div>
                        ) : null}

                        <iframe
                            srcDoc={reportHtml}
                            title="Report Live Preview"
                            className="w-full h-full border-none bg-white"
                        />
                    </div>
                </div>
            </div>

            {/* Toast Notifications */}
            {showToast && (
                <Toast
                    message={toastMessage}
                    type={toastType}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    );
}
